import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { useParams, useSearchParams } from "react-router-dom"
import { createEntryApi, updateEntryApi } from '../api/mobile/entriesApi'
import { getRecord, getTabName } from "../api/mobile/recordsApi"
import { mapDescendantRecords, updateEntryHelper } from '../utilities/mobileRecordHelper'

/* Actions */
const UPDATE_STATE = 'UPDATE_STATE'
const INIT = 'INIT'
const UPDATE_ENTRY = 'UPDATE_ENTRY'

/* Initial State */

const initialState = {
  rootRecord: {},
  descendantRecords: {},
  formTemplates: {},
  mobileGisPdfTemplates: []
}

const initState = ({ rootRecord = {}, descendantRecords = [], formTemplates, mobileGisPdfTemplates }) => ({
  ...initialState,
  rootRecord: rootRecord,
  descendantRecords: mapDescendantRecords(descendantRecords),
  formTemplates: formTemplates,
  mobileGisPdfTemplates: mobileGisPdfTemplates
})

/* Reducer */
const mobileRecordReducer = (state, action) => {
  switch (action?.type) {
    case UPDATE_STATE:
      return { ...state, ...action.payload }
    case INIT:
      return { ...state, ...initState(action.initState) }
    case UPDATE_ENTRY:
      const {
        updatedRootRecord,
        updatedDescendantRecords
      } = updateEntryHelper(state.rootRecord, state.descendantRecords, action.payload.entry, action.payload.map)
      return { ...state, rootRecord: updatedRootRecord, descendantRecords: updatedDescendantRecords }
    default:
      return state
  }
}

/* Contexts */

const MobileRecordContext = createContext(initialState)

const MobileRecordApiContext = createContext({
  updateState: () => {},
  updateEntry: () => {}
})

/* Provider */
export const MobileRecordProvider = ({ children }) => {
  const { projectId, noteId } = useParams()
  let [searchParams, setSearchParams] = useSearchParams()
  const tabName = useMemo(() => searchParams.get("tab"), [searchParams])

  const [state, dispatch] = useReducer(mobileRecordReducer, initialState)

  const api = useMemo(() => {
    const updateState = (field, value) => dispatch({ type: UPDATE_STATE, payload: { [field]: value } })

    const updateEntry = (entry, map) => {
      const apiCall = entry?.id ? updateEntryApi : createEntryApi
      apiCall(entry).then(entryRes => dispatch({ type: UPDATE_ENTRY, payload: { entry: entryRes, map: map } }))
    }

    return { updateState, updateEntry }
  }, [])

  useEffect(() => {
    noteId && getRecord(projectId, noteId)
      .then(res => {
        void dispatch({
          type: INIT,
          initState: {
            rootRecord: res?.root_record,
            descendantRecords: res?.descendant_records,
            formTemplates: res?.mobile_forms,
            mobileGisPdfTemplates: res?.mobile_gis_pdf_templates
          }
        })
      })
    tabName && getTabName(projectId, tabName)
      .then(res => {
        void dispatch({
          type: INIT,
          initState: {
            rootRecord: res?.root_record,
            descendantRecords: res?.descendant_records,
            formTemplates: res?.mobile_forms,
            mobileGisPdfTemplates: res?.mobile_gis_pdf_templates
          }
        })
      })
  }, [noteId, tabName])

  return (
    <MobileRecordApiContext.Provider value={api}>
      <MobileRecordContext.Provider value={state}>
        {children}
      </MobileRecordContext.Provider>
    </MobileRecordApiContext.Provider>
  )
}

/* Custom Context Hooks */
export const useMobileRecordContext = () => useContext(MobileRecordContext)
export const useMobileRecordApi = () => useContext(MobileRecordApiContext)
